import Box from '@mui/material/Box';
import type { ThemeTokens } from '@/theme';

interface ThemePreviewProps {
  tokens: ThemeTokens;
  /** Compact variant for small tiles; full detail for the main panel. */
  dense?: boolean;
}

/**
 * A miniature of the app shell drawn straight from a token set: top bar,
 * sidebar, a card with status colours, a table, inputs and buttons. It is a
 * picture, not an application, so it is hidden from assistive tech and
 * ignores pointer input.
 */
export function ThemePreview({ tokens: t, dense = false }: ThemePreviewProps) {
  const scale = dense ? 0.62 : 1;
  const px = (n: number) => Math.max(1, Math.round(n * scale));
  const bar = (width: number | string, height: number, background: string, opacity = 1) => (
    <Box sx={{ width, height: px(height), borderRadius: 99, background, opacity, flexShrink: 0 }} />
  );

  return (
    <Box
      aria-hidden
      sx={{
        borderRadius: `${t.radius.lg}px`,
        overflow: 'hidden',
        width: '100%',
        border: `1px solid ${t.border.default}`,
        background: t.surface.canvas,
        boxShadow: dense ? 'none' : t.elevation.e2,
        fontFamily: t.typography.fontFamily,
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          height: px(34),
          background: t.component.nav.background,
          borderBottom: `1px solid ${t.component.nav.border}`,
          display: 'flex',
          alignItems: 'center',
          px: `${px(10)}px`,
          gap: `${px(8)}px`,
        }}
      >
        <Box
          sx={{
            width: px(16),
            height: px(16),
            borderRadius: `${t.radius.sm}px`,
            background: t.brand.primary.main,
          }}
        />
        {bar(px(52), 6, t.component.nav.text, 0.85)}
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{ width: px(14), height: px(14), borderRadius: 99, background: t.brand.accent.main }}
        />
      </Box>

      <Box sx={{ display: 'flex', minHeight: px(190) }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: px(74),
            flexShrink: 0,
            background: t.component.sidebar.background,
            borderRight: `1px solid ${t.component.sidebar.border}`,
            py: `${px(8)}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${px(3)}px`,
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => {
            const active = i === 1;
            return (
              <Box
                key={i}
                sx={{
                  mx: `${px(6)}px`,
                  px: `${px(6)}px`,
                  height: px(18),
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${px(6)}px`,
                  borderRadius: `${t.radius.sm}px`,
                  background: active ? t.component.sidebar.activeBackground : 'transparent',
                }}
              >
                <Box
                  sx={{
                    width: px(7),
                    height: px(7),
                    borderRadius: 0.5,
                    background: active
                      ? t.component.sidebar.activeText
                      : t.component.sidebar.mutedText,
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    height: px(4),
                    borderRadius: 99,
                    background: active ? t.component.sidebar.activeText : t.component.sidebar.text,
                    opacity: active ? 1 : 0.5,
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: `${px(10)}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${px(9)}px`,
          }}
        >
          {/* Card: heading, secondary text, status set */}
          <Box
            sx={{
              background: t.surface.paper,
              border: `1px solid ${t.border.subtle}`,
              borderRadius: `${t.radius.md}px`,
              boxShadow: t.elevation.e1,
              p: `${px(9)}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: `${px(6)}px`,
            }}
          >
            {bar('42%', 7, t.text.primary)}
            {bar('72%', 5, t.text.secondary)}
            <Box sx={{ display: 'flex', gap: `${px(4)}px`, mt: `${px(2)}px` }}>
              {[t.status.success, t.status.warning, t.status.error, t.status.info].map((s) => (
                <Box
                  key={s.main}
                  sx={{
                    px: `${px(7)}px`,
                    height: px(13),
                    borderRadius: `${t.radius.pill}px`,
                    background: s.main,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {bar(px(12), 3, s.contrastText, 0.9)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Table: header, plain row, selected row, striped row */}
          <Box
            sx={{
              border: `1px solid ${t.component.table.border}`,
              borderRadius: `${t.radius.md}px`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: px(15),
                background: t.component.table.headerBackground,
                display: 'flex',
                alignItems: 'center',
                gap: `${px(8)}px`,
                px: `${px(7)}px`,
              }}
            >
              {[26, 18, 14].map((w) => (
                <Box key={w}>{bar(`${w}%`, 4, t.component.table.headerText)}</Box>
              ))}
            </Box>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  height: px(15),
                  background:
                    i === 1
                      ? t.component.table.rowSelected
                      : i % 2 === 0
                        ? t.component.table.rowBackground
                        : t.component.table.rowAltBackground,
                  borderTop: `1px solid ${t.component.table.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${px(8)}px`,
                  px: `${px(7)}px`,
                }}
              >
                {bar('30%', 4, t.text.secondary, 0.7)}
                {bar('20%', 4, t.text.secondary, 0.7)}
                {bar('12%', 4, t.text.secondary, 0.7)}
              </Box>
            ))}
          </Box>

          {!dense && (
            <>
              {/* Inputs: resting and focused */}
              <Box sx={{ display: 'flex', gap: `${px(8)}px` }}>
                <Box
                  sx={{
                    flex: 1,
                    height: px(24),
                    background: t.component.input.background,
                    border: `1px solid ${t.component.input.border}`,
                    borderRadius: `${t.component.button.radius}px`,
                    display: 'flex',
                    alignItems: 'center',
                    px: `${px(8)}px`,
                  }}
                >
                  {bar('45%', 4, t.component.input.placeholder)}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: px(24),
                    background: t.component.input.background,
                    border: `2px solid ${t.component.input.focusBorder}`,
                    borderRadius: `${t.component.button.radius}px`,
                    display: 'flex',
                    alignItems: 'center',
                    px: `${px(8)}px`,
                  }}
                >
                  {bar('60%', 4, t.component.input.text)}
                </Box>
              </Box>

              {/* Buttons: contained, outlined, text */}
              <Box sx={{ display: 'flex', gap: `${px(6)}px`, alignItems: 'center' }}>
                <Box
                  sx={{
                    px: `${px(12)}px`,
                    height: px(22),
                    borderRadius: `${t.component.button.radius}px`,
                    background: t.component.button.primaryBackground,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {bar(px(28), 4, t.component.button.primaryText)}
                </Box>
                <Box
                  sx={{
                    px: `${px(12)}px`,
                    height: px(22),
                    borderRadius: `${t.component.button.radius}px`,
                    border: `1px solid ${t.component.button.secondaryBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {bar(px(24), 4, t.component.button.secondaryText)}
                </Box>
                <Box
                  sx={{ px: `${px(10)}px`, height: px(22), display: 'flex', alignItems: 'center' }}
                >
                  {bar(px(20), 4, t.component.button.ghostText)}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
